import { useLayoutEffect, useMemo } from 'react';

import { bind } from 'bind-event-listener';
import type { PreloadedQuery } from 'react-relay';

const canOptimiseResultsRendering = () =>
	typeof window !== 'undefined' && Boolean(window.requestIdleCallback);

type useRenderResultsOptimisationArgs<TEdge> = {
	bodyRef: React.RefObject<HTMLDivElement> | undefined;
	edges: TEdge[];
	maxResultsToShow: number | undefined;
	setMaxResultsToShow: (value: React.SetStateAction<number | undefined>) => void;
	queryReference: PreloadedQuery<any>;
};

/**
 * Optimises the rendering of results by returning a subset of the results
 * initially, based on the maxResultsToShow value. When the main thread is
 * idle, the returned array of edges will update to include the remaining
 * results.
 *
 * Note that when the requestIdleCallback is not available, we will render
 * this hook no-ops, and the original array of edges will be returned.
 */
export const useRenderResultsOptimisation: <TEdge>({
	bodyRef,
	edges,
	maxResultsToShow,
	setMaxResultsToShow,
	queryReference,
}: useRenderResultsOptimisationArgs<TEdge>) => TEdge[] = <TEdge>({
	bodyRef,
	edges,
	maxResultsToShow,
	setMaxResultsToShow,
	queryReference,
}: useRenderResultsOptimisationArgs<TEdge>) => {
	const edgesToDisplay = useMemo(() => {
		return canOptimiseResultsRendering() && maxResultsToShow && maxResultsToShow < edges.length
			? edges.slice(0, maxResultsToShow)
			: edges;
	}, [edges, maxResultsToShow]);

	useLayoutEffect(() => {
		if (!canOptimiseResultsRendering()) {
			return;
		}

		if (!bodyRef?.current) {
			return;
		}

		// If the user tries to scroll the search results list, render all
		// results immediately
		const unbind = bind(bodyRef.current, {
			type: 'scroll',
			listener: () => {
				setMaxResultsToShow(undefined);
			},
			options: {
				once: true,
			},
		});

		// When the main thread is idle, render the rest of the results
		window.requestIdleCallback(() => {
			unbind();
			setMaxResultsToShow(undefined);
		});

		return unbind;
	}, [bodyRef, setMaxResultsToShow, queryReference]);

	return edgesToDisplay;
};
